// Empire Apps Chassis · identity-zkp · Verifiable Presentation builder + verifier
// Bundles VCs + predicate proofs + holder signature into a single VP that the
// holder can hand to a verifier (clinic, landlord, bank, notary).

import type {
  DIDString,
  Ed25519PrivateKey,
  Predicate,
  VerifiableCredentialV2,
  VerifiablePresentationV2,
  VerifierChallenge,
  VerifyResult,
} from './types.ts';
import { createPresentation, verifyPresentation, signExtendedVP } from './vc.ts';
import { deriveBundle, type DisclosureBundle } from './selective-disclosure.ts';
import type { BBSSignedCredential } from './bbs-plus.ts';
void createPresentation;

export interface BuildVPOptions {
  readonly holder: DIDString;
  readonly holderPrivateKey: Ed25519PrivateKey;
  readonly credentials: readonly VerifiableCredentialV2[];
  readonly bbsCredentials?: readonly BBSSignedCredential[];
  readonly predicates?: readonly Predicate[];
  readonly challenge?: VerifierChallenge;
}

export interface ExtendedVP extends VerifiablePresentationV2 {
  readonly disclosureBundles?: readonly DisclosureBundle[];
  readonly respondsTo?: VerifierChallenge;
}

export async function buildVP(opts: BuildVPOptions): Promise<ExtendedVP> {
  const bundles: DisclosureBundle[] = [];
  if (opts.bbsCredentials && opts.predicates) {
    for (const c of opts.bbsCredentials) {
      bundles.push(await deriveBundle(c, opts.predicates));
    }
  }
  // Build the unsigned skeleton including bundles/challenge so the
  // holder signature covers the full disclosure surface.
  const skeleton: Omit<ExtendedVP, 'proof'> = {
    '@context': [
      'https://www.w3.org/ns/credentials/v2',
      'https://w3id.org/security/data-integrity/v2',
    ],
    id: `urn:uuid:${crypto.randomUUID()}`,
    type: ['VerifiablePresentation'],
    holder: opts.holder,
    verifiableCredential: opts.credentials,
    ...(bundles.length > 0 ? { disclosureBundles: bundles } : {}),
    ...(opts.challenge ? { respondsTo: opts.challenge } : {}),
  };
  return signExtendedVP(skeleton, opts.holder, opts.holderPrivateKey);
}

export async function verifyVP(vp: ExtendedVP): Promise<VerifyResult> {
  const v = await verifyPresentation(vp);
  if (!v.ok) {
    return { ok: false, reason: 'signature-invalid' };
  }
  const disclosed: Record<string, string | number | boolean> = {};
  if (vp.disclosureBundles) {
    for (const bundle of vp.disclosureBundles) {
      for (const proof of bundle.proofs) {
        if (!proof.result) {
          return { ok: false, reason: 'predicate-failed' };
        }
        for (const [k, val] of Object.entries(proof.partialReveal)) {
          if (val !== undefined) disclosed[k] = val;
        }
      }
    }
  }
  if (vp.respondsTo) {
    const now = Date.now();
    if (Date.parse(vp.respondsTo.expiresAt) < now) {
      return { ok: false, reason: 'expired' };
    }
  }
  return { ok: true, subject: vp.holder, disclosed };
}

export function newChallenge(
  verifierDid: DIDString,
  purpose: string,
  predicates: readonly Predicate[],
  ttlMs = 5 * 60 * 1000,
): VerifierChallenge {
  const now = new Date();
  return {
    verifierDid,
    nonce: crypto.randomUUID(),
    purpose,
    issuedAt: now.toISOString(),
    expiresAt: new Date(now.getTime() + ttlMs).toISOString(),
    predicates,
  };
}
